<template>
  <div class="budz-report relative flex flex-col">
    <div class="w-full">
      <report-header
        :error.sync="error"
        :range="range"
        :report="report"
        @getReport="getReport"
      >
        <template #title>
          budz report
        </template>
        <template #subtitle>
          compare and analyze the performance of your campaigns
        </template>
      </report-header>
    </div>

    <div v-if="loadingReport" class="flex justify-center align-center mt-12">
        <loading-icon />
    </div>

    <template v-else-if="report">
      <div class="text-3xl mb-8">program overview</div>
      <div class="overview-container grid text-center">
        <div class="px-2">
          <div class="overview-title font-secondary flex justify-center items-center mb-2">customers joined<br />via referral</div>
          <div class="text-2xl">{{ total_customers }}</div>
        </div>
        <div class="left-border px-2">
          <div class="overview-title font-secondary flex justify-center items-center mb-2">referrals who<br />made a visit</div>
          <div class="text-2xl">{{ unique_members_with_visits }}</div>
        </div>
        <div class="left-border px-2">
          <div class="overview-title font-secondary flex justify-center items-center mb-2">total referral visits</div>
          <div class="text-2xl">{{ total_referral_visits }}</div>
        </div>
        <div class="left-border px-2">
          <div class="overview-title font-secondary flex justify-center items-center mb-2">average number<br />of visits</div>
          <div class="text-2xl">{{ average_visit }}</div>
        </div>
        <div class="left-border px-2">
          <div class="overview-title font-secondary flex justify-center items-center mb-2">total spend</div>
          <div class="text-2xl">${{ total_spend }}</div>
        </div>
        <div class="left-border px-2">
          <div class="overview-title font-secondary flex justify-center items-center mb-2">average<br />spend</div>
          <div class="text-2xl">${{ average_spend }}</div>
        </div>
      </div>
      <div class="overview-divider w-10/12 mx-auto my-4" />
      <div class="text-3xl mb-4 mt-8">top 5 promoters</div>
      <div class="w-11/12">
        <sb-custom-table
          :rows="topPromoters"
          :columns="topPromotersColumns"
          class="top-promoters"
          disableTableSort
        >
          <template #member_id="item">
            <a :href="`/members/${item.row.member_id}`" target="_blank" >
              {{item.row[item.column.key]}}
            </a>
          </template>
        </sb-custom-table>
        <div class="overview-divider w-10/12 mx-auto my-4" />
      </div>
      <div class="text-3xl mb-4 mt-8">latest referrals</div>
      <div class="w-11/12">
        <sb-custom-table
          :rows="tableRows"
          :columns="tableColumns"
          disableTableSort
        >
          <template #referrer_member_id="item">
            <a :href="`/members/${item.row.referrer_member_id}`" target="_blank">
              {{item.row[item.column.key]}}
            </a>
          </template>
          <template #referred_member_id="item">
            <a :href="`/members/${item.row.referred_member_id}`" target="_blank">
              {{item.row[item.column.key]}}
            </a>
          </template>
        </sb-custom-table>
        <div v-if="pagesTotal > 1" class="flex justify-center items-center mt-10">
          <pagination
              :page.sync="page"
              :pages="pagesTotal"
              :gap="2"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { subDays } from 'date-fns';
import { utcToZonedTime } from "date-fns-tz";
import { mapGetters } from 'vuex'
import { SbCustomTable, Pagination, LoadingIcon } from '@springbig/sb-components';
import { formatPhoneNumber, truncateWithEllipses, commafyNumber } from '../../utilities/index';
import { reportsAPI } from '../../services/api/reportsAPI';
import ReportHeader from "./ReportHeader.vue";

export default {
  name: 'BudzReport',
  components: {
    ReportHeader,
    SbCustomTable,
    LoadingIcon,
    Pagination,
  },
  data() {
    return {
      range: null,
      page: 0,
      report: null,
      average_spend: null,
      average_visit: null,
      total_customers: null,
      total_referral_visits: null,
      total_spend: null,
      unique_members_with_visits: null,
      topPromoters: [],
      topPromotersColumns: [
        { key: "member_id", value: "member id" },
        { key: "customer", value: "customer" },
        { key: "phone_number", value: "phone number" },
        { key: "total_members_joined_via_referral", value: "# of members \n joined via referral" }
      ],
      tableRows: [],
      loadingReport: false,
      tableColumns: [
        { key: "referral_create_date", value: "date" },
        { key: "referrer_member_id", value: "referrer member id" },
        { key: "referrer", value: "referrer" },
        { key: "referred_member_id", value: "referred member id" },
        { key: "referred_member", value: "referred member" }
      ],
      error: {},
    }
  },
  async created() {
    await this.$store.dispatch('LOAD_CURRENT_MERCHANT');
    this.range = this.defaultDateRange();
  },
  watch: {
    page() {
      this.getReport();
    }
  },
  computed: {
    pagesTotal(){
      return this.report?.pagination?.total_pages || 1;
    },
    ...mapGetters(['merchantTimeZone'])
  },
  methods: {
    defaultDateRange() {
      const currentDate = utcToZonedTime(new Date(), this.merchantTimeZone);
      const minimumDate = subDays(currentDate, 90);

      return {
        min: minimumDate,
        start: minimumDate,
        max: subDays(currentDate, 1),
        end: subDays(currentDate, 1)
      }
    },
    formatReportData() {
      const {
        results,
        top_promoters,
        overview_kpis,
      } = this.report.output_data;
      this.topPromoters = top_promoters.map(item => ({
        ...item,
        "customer": truncateWithEllipses(item["customer"], 68),
        "phone_number": formatPhoneNumber(item["phone_number"]),
      }));

      Object.keys(overview_kpis).forEach(key => this[key] = commafyNumber(overview_kpis[key]) )

      if (results.length > 0) {
        this.tableRows = results.map(result => ({
          ...result,
          referred_member: truncateWithEllipses(result["referred_member"], 68),
          referrer: truncateWithEllipses(result["referrer"], 68),
          referral_create_date: result["referral_create_date"]
        }));
      }
    },
    createNewReport() {
      return reportsAPI.createReport('Reports::BudzReport', this.range.start, this.range.end);
    },
    fetchExistingReport() {
      return reportsAPI.getReportById(this.report.id, this.page + 1);
    },
    async getReport(newReport) {
      this.loadingReport = true;
      try {
        const { report } = !this.report?.id || (this.report.id && newReport) ? await this.createNewReport() : await this.fetchExistingReport();
        this.report = report;
        this.formatReportData();
      } catch(error){
        this.error.message = "error, please refresh or contact support";
      } finally {
        this.loadingReport = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.budz-report {
  .overview-title {
    min-height: 60px;
  }
  .overview-container {
    grid-template-columns: 1.2fr 1fr 1fr 1fr 1fr 1fr;
    .left-border {
      border-left: 1px solid rgba(0, 122, 255, 0.3);
    }
  }
  .overview-divider {
    border-top: 1px solid rgba(0, 122, 255, 0.3);
  }
  a {
    color: #6FC1FF;
  }
}
</style>
